import { useState, useEffect } from "react";
import "./App.css";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

function App() {
  const [webhook, setWebhook] = useState(
    localStorage.getItem("discord_webhook") || ""
  );
  const threshold = 10; // Fixed threshold at 10%
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (webhook) {
      fetchSubscriptions();
    }
  }, [webhook]);

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchSubscriptions = async () => {
    if (!webhook) return;

    try {
      const response = await fetch(
        `${API_URL}/api/subscriptions?webhook=${encodeURIComponent(webhook)}`
      );
      if (!response.ok) throw new Error("Failed to fetch subscriptions");
      const data = await response.json();
      setSubscriptions(data.subscriptions?.items || []);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: searchQuery }),
      });

      if (!response.ok) throw new Error("Search failed");

      const data = await response.json();
      // Filter out "featured" items in frontend as well
      const filteredResults = (data.results || []).filter(
        (item) => item.name.toLowerCase() !== "featured"
      );
      setSearchResults(filteredResults);

      if (filteredResults.length === 0) {
        showToast("No items found", "warning");
      }
    } catch (err) {
      setError(err.message);
      showToast("Search failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (item) => {
    if (!webhook) {
      showToast("Please enter your Discord webhook first", "warning");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscriptions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          webhook,
          itemId: item.id,
          itemName: item.name,
          threshold,
        }),
      });

      if (!response.ok) throw new Error("Failed to add subscription");

      showToast(`Following ${item.name}`, "success");
      fetchSubscriptions();
    } catch (err) {
      showToast("Failed to follow item", "error");
    }
  };

  const handleUnfollow = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ webhook, itemId }),
      });

      if (!response.ok) throw new Error("Failed to remove subscription");

      showToast("Unfollowed item", "success");
      fetchSubscriptions();
    } catch (err) {
      showToast("Failed to unfollow item", "error");
    }
  };

  const saveWebhook = () => {
    localStorage.setItem("discord_webhook", webhook);
    showToast("Webhook saved", "success");
    if (webhook) fetchSubscriptions();
  };

  const isFollowing = (itemId) => {
    return subscriptions.some((sub) => sub.id === itemId);
  };

  return (
    <div className="App">
      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.message}</div>
      )}

      <header className="header">
        <h1>🪙 OSRS Items Price Tracker</h1>
        <div className="description-box">
          <h3>Get Discord alerts when item prices change</h3>
          <div className="steps">
            <div className="step">
              <span className="step-number">1</span>
              <span className="step-text">Add your Discord webhook</span>
            </div>
            <div className="step">
              <span className="step-number">2</span>
              <span className="step-text">Search and follow items</span>
            </div>
            <div className="step">
              <span className="step-number">3</span>
              <span className="step-text">Receive automatic notifications</span>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <section className="config-section">
          <h2>Configuration</h2>
          <div className="config-form">
            <div className="form-group">
              <label htmlFor="webhook">Discord Webhook URL</label>
              <input
                type="url"
                id="webhook"
                value={webhook}
                onChange={(e) => setWebhook(e.target.value)}
                placeholder="https://discord.com/api/webhooks/..."
              />
              <small>
                <a
                  href="https://support.discord.com/hc/en-us/articles/228383668-Intro-to-Webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  How to get your Discord webhook
                </a>
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="threshold">Price Change Threshold</label>
              <p className="threshold-value">10%</p>
              <small>
                You will be notified when price changes by 10% or more
              </small>
            </div>
            <button onClick={saveWebhook} className="btn btn-primary">
              Save Configuration
            </button>
          </div>
        </section>

        <section className="search-section">
          <h2>Search Items</h2>
          <form onSubmit={handleSearch} className="search-form">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for OSRS items..."
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </form>

          {error && <div className="error">{error}</div>}

          {searchResults.length > 0 && (
            <div className="search-results">
              {searchResults.map((item) => (
                <div key={item.id} className="result-card">
                  <span className="item-name">{item.name}</span>
                  {isFollowing(item.id) ? (
                    <button
                      onClick={() => handleUnfollow(item.id)}
                      className="btn btn-secondary"
                    >
                      Unfollow
                    </button>
                  ) : (
                    <button
                      onClick={() => handleFollow(item)}
                      className="btn btn-primary"
                    >
                      Follow
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="subscriptions-section">
          <h2>Followed Items ({subscriptions.length})</h2>
          {subscriptions.length === 0 ? (
            <p className="empty-state">
              No items followed yet. Search and follow items to track their
              prices.
            </p>
          ) : (
            <div className="subscriptions-list">
              {subscriptions.map((item) => (
                <div key={item.id} className="subscription-card">
                  <div className="subscription-info">
                    <h3>{item.name}</h3>
                    <p>Threshold: {item.threshold}%</p>
                    {item.priceHistory.length > 0 && (
                      <p className="last-price">
                        Last price:{" "}
                        {item.priceHistory[
                          item.priceHistory.length - 1
                        ].price.toLocaleString()}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleUnfollow(item.id)}
                    className="btn btn-danger"
                  >
                    Unfollow
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      <footer className="footer">
        <p>
          Price checks every hour • Discord notifications on significant changes
        </p>
      </footer>
    </div>
  );
}

export default App;
